// Imports
import { isDevelopment, sendXHR, failedRequest, debouncer, getViewport, makeDate, makeDateNow } from "src/lib/utils";
import { gu_auth } from "src/lib/authentication.js";
import { GATEKEEPER_ENDPOINTS } from "src/lib/data.js";
import { getStorage, setStorage, isObjectValid } from "src/lib/storage";
import { sq_personalise } from "src/lib/personalisation.js";
import { ms_api } from 'src/lib/microsoft_graph.js';

export const PortalContext = React.createContext();


// Stateful components

// Dashboard state management

export class PortalProvider extends React.Component {

    //
    // Lifecycle
    // 

    constructor(props) {
        super(props);

        this['state'] = {
            'viewport': 1,
            'page_error': '',
            'page_not_found': false,
            'is_loading': true,
            'refresh_page': false,
            'is_refreshing': false,
            'current_page': '',
            'selected_date': makeDateNow(),
            'my_schedule': [],
            'my_exams': [],
            'show_modal': false,
            'modal_content': null,
            'all_pages': [],
            'configuration': null,
            'user_personalisation': null,
            'notification_count': null,
            'unread_emails_count': 0,
            'staff_mode': false,
            'helpers': {
                'updatePersonalisation': this.updatePersonalisation.bind(this),
                'updateSelectedDate': this.updateSelectedDate.bind(this),
                'setGatekeeperData': this.setGatekeeperData.bind(this),
                'setModalContent': this.setModalContent.bind(this),
                'closeModal': this.closeModal.bind(this),
                'closeMobileNav': this.closeMobileNav.bind(this),
                'getDynamicLinkURL': this.getDynamicLinkURL.bind(this),
                'refreshPage': this.refreshPage.bind(this),
                'getChildPages': this.getChildPages.bind(this),
                'getPageBySlug': this.getPageBySlug.bind(this),
                'getDynamicLinkUrlById': this.getDynamicLinkUrlById.bind(this),
                'getDynamicLinkObject': this.getDynamicLinkObject.bind(this),
                'setColoursToStorage': this.setColoursToStorage.bind(this),
                'updateNotificationCount': this.updateNotificationCount.bind(this),
                'toggleStaffMode': this.toggleStaffMode.bind(this),
                'generateStudentCentreUrl': this.generateStudentCentreUrl.bind(this),
                'getGatekeeperList': this.getGatekeeperList.bind(this),
                'checkForGatekeeperData': this.checkForGatekeeperData.bind(this),
                'processGatekeeperSources': this.processGatekeeperSources.bind(this),
                'updateMySchedule': this.updateMySchedule.bind(this)
            }
        };

        // Binds
        this.checkViewport = this.checkViewport.bind(this);
        this.processPortalPages = this.processPortalPages.bind(this);
        this.locationHashChanged = this.locationHashChanged.bind(this);
        this.ssoCallback = this.ssoCallback.bind(this);
        this.ssoErrorCallback = this.ssoErrorCallback.bind(this);
        this.configurePortal = this.configurePortal.bind(this);
        this.processGatekeeperData = this.processGatekeeperData.bind(this);
        this.allDataFound = this.allDataFound.bind(this);
        this.setUnreadEmailsCount = this.setUnreadEmailsCount.bind(this);
    }

    componentDidMount() {
        var this_component = this;

        // Get portal configuration
        this_component.getPortalConfiguration();

        // Check for 404 page
        var page_wrapper = document.getElementById('react__app-wrapper');
        if (page_wrapper.classList.contains('page-404')) {
            
            this_component.setState({
                page_not_found: true
            });

        } else {

             // Listen for hashchange if NOT 404 page
            window.addEventListener('hashchange', this_component.locationHashChanged);
        }

       // Listen for resize
        window.addEventListener('resize', debouncer(this_component.checkViewport, 100));
    }

    componentDidUpdate(prev_props, prev_state) {
        var this_component = this;

        var page_not_found = this_component['state']['page_not_found'];
        var is_loading = this_component['state']['is_loading'];
        if(is_loading !== prev_state['is_loading']) {
            if(!is_loading && !page_not_found) {
                // The portal has loaded
                this_component.getCurrentPage();

                // Process emails
                if(!isDevelopment()) {
                    var mailSuccess = this_component.setUnreadEmailsCount;
                    ms_api.fn.getEmails(mailSuccess);
                }
            }
        }

        // New data
        var refresh_page = this_component['state']['refresh_page'];
        if(refresh_page !== prev_state['refresh_page']) {
            if(refresh_page) {
                // The page has been refreshed. Cancel the flag
                this_component.setState({
                    'refresh_page': false,
                    'is_refreshing': false
                });
            }
        }
    }


    //
    // Helpers
    //

    locationHashChanged() {
        var this_component = this;

        // Manually close the modal
        this_component.closeModalManual();

        // Get the current page
        this_component.getCurrentPage();
    }

    updatePersonalisation(user_personalisation) {
        var this_component = this;

        this_component.setState({
            'user_personalisation': user_personalisation
        });
    }

    // Get API data that's saved in the browser storage
    getStoredData(data_source) {
        var url_components = data_source.split(',');
        var endpoint = url_components[0];
        
        var stored_data = squiz.fn.getStorage(endpoint, 'session', '');

        if(stored_data !== null) {
            // Check if the stored data has passed its expiry
            if(GATEKEEPER_ENDPOINTS[endpoint]){
                var data_expiry = GATEKEEPER_ENDPOINTS[endpoint]['age'];
                var json_data = JSON.parse(stored_data);
    
                if(isObjectValid(json_data, data_expiry)) {
                    return stored_data;
                }
            }
        }

        // Default response
        return null;
    }

    updateSelectedDate(date) {
        var this_component = this;

        this_component.setState({
            'selected_date': date
        });
    }

    updateMySchedule(schedule_data) {
        var this_component = this;

        this_component.setState({
            'my_schedule': schedule_data['my_schedule'],
            'my_exams': schedule_data['my_exams']
        });
    }

    closeMobileNav(){
        var mobile_toggle = document.querySelector('.header__mobile-toggle');
        var nav_panel = document.querySelector('.nav-panel');
        if(mobile_toggle && nav_panel){
            mobile_toggle.classList.remove('active');
            nav_panel.classList.remove('active');
        }
    }

    getDynamicLinkURL(content_link) {
        var this_component = this;
        var portal_links = this_component['state']['configuration']['portal_links'];
        var dynamic_url = content_link;

        if(content_link.indexOf('http') !== 0) {
            var content_links = portal_links['content'];

            Object.keys(content_links).forEach(function(link_group){
                content_links[link_group].forEach(function(link){
                    var link_id = link['id'];
                    var link_url = link['url'];
                    var link_lookup = link['lookup_url'];

                    if(content_link === link_lookup) {
                        if (link['group'] === 'peoplesoft') {
                            dynamic_url = this_component.generateStudentCentreUrl(link_id);
                        } else {
                            dynamic_url = link_url;
                        }
                    }
                });
            });
        }

        return dynamic_url;
    }

    // pass in an asset id of a link asset contained within the portal_links object
    // returns the URL
    getDynamicLinkUrlById(content_link_id, content_link_value_type){
        var this_component = this;
        var portal_links = this_component['state']['configuration']['portal_links'];
        var dynamic_url = null;

        if(typeof content_link_id != 'undefined'){
            var content_links = portal_links['content'];

            Object.keys(content_links).forEach(function(link_group){
                content_links[link_group].forEach(function(link){
                    var link_id = link['id'];
                    var link_url = link['url'];
                    var link_lookup = link['lookup_url'];

                    if(content_link_id === link_id) {

                        if (link['group'] === 'peoplesoft') {
                            dynamic_url = this_component.generateStudentCentreUrl(link_id);
                        } else {
                            dynamic_url = link_url;

                            if(typeof content_link_value_type != 'undefined'){
                                if(content_link_value_type == 'lookup_url'){
                                    dynamic_url = link_lookup;
                                }
                            }
                        }
                    }
                });
            });
        } else {
            return null;
        }

        return dynamic_url;
    }

    getDynamicLinkObject(content_link_match, dynamic_key, backup_key){
        var this_component = this;
        var portal_links = this_component['state']['configuration']['portal_links'];
        var dynamic_obj = null;

        if(typeof content_link_match != 'undefined'){
            var content_links = portal_links['content'];

            Object.keys(content_links).forEach(function(link_group){
                content_links[link_group].forEach(function(link){
                    var key_match = '';
                    var link_id = link['id'];
                    // match on the passed in key
                    // allows for finding a link object based on a specified key value
                    // for example, instead of link['id'] (default), you might want to match on the path value
                    // link['path']

                    // if a key has been passed in
                    if(dynamic_key){
                        // match based on a specified key value
                        if(content_link_match == link[dynamic_key]){
                            // match.
                            dynamic_obj = link;
                        } else {
                            // PeopleSoft URL Edge Case
                            if(link['group'] === 'peoplesoft') {
                                if(link[backup_key].indexOf(content_link_match) !== -1) {
                                    dynamic_obj = link;
                                }
                            }                            
                        }
                    } else {
                        // as a default, we're matching on the link asset ID
                        if(content_link_match === link_id) {
                            // match.
                            dynamic_obj = link;
                        }
                    }
                });
            });
        }

        return dynamic_obj;
    }
    
    generateStudentCentreUrl(id){
        var this_component = this;
        // the final returned value
        // eg: #/student-centre/path-of-editor
        var sc_url = null;
        var source_id = id;
        
        // get the portal links object
        var portal_links = this_component['state']['configuration']['portal_links'];
        // get the base student center URL
        var student_centre_page_url = portal_links['studentCentre']['url'];
        // if a source ID has been passed in
        if(source_id){
            // use the ID as the base deeplink path
            var student_centre_path_name = source_id;
            // nowfind the peoplesoft link object
            // we'll use a firendly pathname if it exists
            var source_object = this_component.getDynamicLinkObject(source_id);
            // check if there is a link object,
            if(source_object){
                // make sure the 'path' key is present and not empty
                if(source_object['path'] && source_object['path'] != ''){
                    // apply value
                    student_centre_path_name = source_object['path'];
                }
            }
            // set the base student centre url with additional path name
            sc_url = student_centre_page_url + '/' + student_centre_path_name;
    
        } else {
            // no source ID found
            // return the base student centre url
            sc_url = student_centre_page_url;
        }

        // return the value
        return sc_url;
        
    }
   
    setUnreadEmailsCount(unread_emails_count) {
        var this_component = this;

        this_component.setState({
            'unread_emails_count': unread_emails_count
        });
    }

    refreshPage() {
        var this_component = this;

        this_component.setState({
            'refresh_page': true,
            'is_refreshing': true
        });
    }

    setColoursToStorage(mapped_course_codes) {
        var this_component = this;
        var colour_code_data = mapped_course_codes;

        // write back to local storage
        // first get latest storage object
        var colour_key = squiz['vars']['store_id'] + 'getallstorage';
        var colour_storage = JSON.parse(getStorage('session', colour_key));
        colour_storage['data']['coursecolours'] = colour_code_data;
        setStorage('session', colour_key, colour_storage);

        //
        // place to write back to gatekeeper API
        //
        var gatekeeper_options = {
            "source": "setcoursecolours,1",
            "data": colour_code_data,
            "success": function(response){
                
            }   
        };
        this_component.setGatekeeperData(gatekeeper_options);
    }

    toggleStaffMode() {
        var this_component = this;
        var new_value = !this_component['state']['staff_mode'];

        this_component.setState({
            'staff_mode': new_value
        });
    }


    //
    // Functions
    //

    // Get the configurations for this environment
    getPortalConfiguration() {
        var this_component = this;
        var endpoints_container = document.querySelector('#endpoints');
        var configuration_data = endpoints_container.getAttribute('data-config');

        var xhr_parameters = {
            'request_url': configuration_data,
            'request_success': this_component.configurePortal,
            'request_failure': failedRequest,
            'request_extras': ""
        };

        sendXHR(xhr_parameters, 'GET');
    }

    // Process the environment configurations
    configurePortal(response) {
        var this_component = this;
        var json_response = JSON.parse(response);

        this_component.setState({
            'configuration': json_response
        });

        // If page not found render message with Error callback
        if (this_component['state']['page_not_found']) {
            
            var pageNotFound = "<h1>Page not found</h1>";
            if (json_response['system_messages'] && json_response['system_messages']['pageNotFound']) {
                var heading = "<h1 class=\"page-header__title\">" + json_response['system_messages']['pageNotFound']['heading'] + "</h1>";
                var content = json_response['system_messages']['pageNotFound']['content'];
                pageNotFound =  heading + content;
            }

            this_component.ssoErrorCallback(pageNotFound);
        
        // Else continue with SSO
        } else {
            
            var simulation_callbacks = {
                'set': this_component.ssoCallback,
                'clear': this_component.ssoCallback
            };
    
            // Set up Griffith auth
            gu_auth.fn.configureSettings(json_response, this_component.ssoCallback, this_component.ssoErrorCallback);
            gu_auth.fn.configureSimulationSettings(simulation_callbacks);
            
    
            // Griffith SSO check
            if(isDevelopment()) {
                this_component.ssoCallback();
            } else {
                gu_auth.fn.checkSSO();
            }
        }


        
    }

    // Once SSO has completed, gather data from the API
    ssoCallback(force_refresh) {
        var this_component = this;

        // Get API data
        var list_type = 'all';
        var successCallback = this_component.allDataFound;
        this_component.checkForGatekeeperData(successCallback, list_type, force_refresh);

        // Check viewport
        if(isDevelopment()) {
            // Allow for styles to be injected locally
            setTimeout(function() {
                this_component.checkViewport();
            }, 1);
        } else {
            this_component.checkViewport();
        }
    }

    // Once SSO has completed, gather data from the API
    ssoErrorCallback(error_message) {
        var this_component = this;

        this_component.setState({
            'page_error': error_message,
            'is_loading': false
        });
    }

    // Check if this a valid page
    isPageValid(page_slug, page_list) {
        var valid_page = false;

        // Look through all pages regardless of attributes
        page_list.forEach(function(page, page_index) {
            var current_slug = page['slug'];

            if(current_slug === page_slug) {
                valid_page = true;
            }
        });

        return valid_page;
    }

    // Get all pages by a certain IA level
    getPagesByIA(ia_level) {
        var this_component = this;
        var all_pages = this_component['state']['all_pages'];
        var page_list = [];
        
        all_pages.forEach(function(page, page_index) {
            var current_ia_level = page['ia_level'];

            if(current_ia_level === ia_level) {
                page_list.push(page);
            }
            
            // Check child pages
            page['children'].forEach(function(child, child_index) {
                var child_ia_level = child['ia_level'];

                if(child_ia_level === ia_level) {
                    page_list.push(child);
                }
            });
        });

        return page_list;
    }

    // Generate a URL to get data from the API
    prepareGatekeeperURL(data_source) {
        var url_components = data_source.split(',');
        var endpoint = url_components[0];
        var gatekeeper_endpoint = GATEKEEPER_ENDPOINTS[endpoint];
        var gatekeeper_url = '';
        if(gatekeeper_endpoint){
            var gatekeeper_data = GATEKEEPER_ENDPOINTS[endpoint]['path'];
            gatekeeper_data.forEach(function(component, component_index) {
                // Replace placeholder values with content
                var endpoint_data = url_components[component_index];
                var gatekeeper_path = component.replace('##', endpoint_data);
                
                gatekeeper_url += gatekeeper_path;
            });
    
            if(isDevelopment()) {
                return './externals/' + endpoint + '.json';
            } else {
                return gu_auth.fn.makeAbsoluteUrl(gatekeeper_url);
            }
        } else {
            return null;
        }
    }

    // Check and set the device's current viewport
    checkViewport() {
        var this_component = this;
        var current_viewport = this_component['state']['viewport'];
        var new_viewport = getViewport();

        if(current_viewport !== new_viewport) {
            this_component.setState({
                'viewport': new_viewport
            });
        }
    }

    // Access data from the API
    checkForGatekeeperData(successCallback, list_type, force_refresh) {
        var this_component = this;
        
        // Force refresh
        if (force_refresh) {
            var storage_keys = Object.keys(sessionStorage);
            var storage_prefix = squiz['vars']['store_id'];
            storage_keys.forEach(function(storage_key) {
                if (storage_key.indexOf(storage_prefix) === 0) {
                    sessionStorage.removeItem(storage_key);
                }
            });
        }

        // Figure out what data we want
        var sources_list = this_component.getGatekeeperList(list_type);
        this_component.processGatekeeperSources(sources_list, force_refresh, successCallback);
    }

    // Check data from source list
    processGatekeeperSources(sources_list, force_refresh, successCallback) {
        var this_component = this;

        var gatekeeper_sources = this_component.getGatekeeperSources(sources_list, force_refresh);        
        
        if(gatekeeper_sources.length === 0) {
            // No data to fetch
            successCallback();
        } else {
            // Fetch from Gatekeeper
            this_component.getGatekeeperData(gatekeeper_sources, successCallback);
        }
    }

    // Get complete list of API endpoints
    getGatekeeperList(list_type) {
        var gatekeeper_container = document.querySelector('#gatekeeper');
        var all_sources = gatekeeper_container.getAttribute('data-sources').split('; ');
        var conditional_sources = gatekeeper_container.getAttribute('data-sources-conditional').split('; ');
        var source_list = [];

        switch(list_type) {
            case 'all':
                source_list = all_sources;
                break;
            case 'service_indicators':
                var source_match = 'getmynegativeserviceindicator';

                conditional_sources.forEach(function(source, source_index) {
                    var source_data = source.split(',');

                    if(source_data[0] === source_match) {
                        source_list.push(source);
                    }
                });

                break;
            case 'checklist':
                var source_match = 'getmychecklist';

                conditional_sources.forEach(function(source, source_index) {
                    var source_data = source.split(',');

                    if(source_data[0] === source_match) {
                        source_list.push(source);
                    }
                });

                break;
            case 'my_results':
                var source_match = 'getmyresults';

                conditional_sources.forEach(function(source, source_index) {
                    var source_data = source.split(',');

                    if(source_data[0] === source_match) {
                        source_list.push(source);
                    }
                });

                break;
        }

        return source_list;
    }

    // Get list of API endpoints to request
    getGatekeeperSources(sources_list, force_refresh) {
        var this_component = this;
        var gatekeeper_sources = [];

        // Check for storage
        sources_list.forEach(function(source, source_index) {
            var stored_data = this_component.getStoredData(source);

            // Force a refresh
            // if(force_refresh) {
            //     stored_data = null;
            //     this_component.setState({
            //         is_refreshing: true
            //     });
            // }

            if(stored_data === null) {
                gatekeeper_sources.push(source);
            }
        });

        console.log('The following sources have expired. Get new ones!');
        console.log(gatekeeper_sources);

        return gatekeeper_sources;
    }

    // Make a request to the API 
    getGatekeeperData(sources_list, successCallback) {
        var this_component = this;
        var all_requests = [];

        sources_list.forEach(function(source, source_index) {
            var xhr_promise = this_component.getGatekeeperDataBySource(source);
            all_requests.push(xhr_promise);

            /*var gatekeeper_endpoint_url = this_component.prepareGatekeeperURL(source);
            if(gatekeeper_endpoint_url){
                xhr_parameters['request_url'] = gatekeeper_endpoint_url;
                xhr_parameters['request_extras'] = {
                    "data_source": source.split(',')[0],
                    "index": source_index
                };
                
                // This is an authenticated request
                var gu_token = gu_auth.fn.getActiveToken();
    
                var xhr_promise = sendXHR(xhr_parameters, 'GET', gu_token);
                all_requests.push(xhr_promise);
            }*/
        });

        // Wait for all requests to complete before continuing
        Promise.all(all_requests).then(function() {
            successCallback();
        });
    }

    // Make a single request to the API
    getGatekeeperDataBySource(source) {
        var this_component = this;
        
        var gatekeeper_endpoint_url = this_component.prepareGatekeeperURL(source);

        if(gatekeeper_endpoint_url){
            var xhr_parameters = {
                'request_url': gatekeeper_endpoint_url,
                'request_success': this_component.processGatekeeperData,
                'request_failure': failedRequest,
                'request_extras': {
                    "data_source": source.split(',')[0]
                }
            };
            
            // This is an authenticated request
            var gu_token = gu_auth.fn.getActiveToken();

            return sendXHR(xhr_parameters, 'GET', gu_token);
        }

        return null;
    }

    // Make a request PUT to the API to save data
    setGatekeeperData(gatekeeper_options) {
        var this_component = this;
        var post_data = (gatekeeper_options['data']) ? gatekeeper_options['data'] : '';
        var post_source = (gatekeeper_options['source']) ? gatekeeper_options['source'] : '';
        var post_starts_with = (gatekeeper_options['startsWithParam']) ? '?startsWith=' + gatekeeper_options['startsWithParam'] : '';
        var success_callback = (gatekeeper_options['success']) ? gatekeeper_options['success'] : null;
        var xhr_parameters = {
            'request_url': this_component.prepareGatekeeperURL(post_source) + post_starts_with,
            'request_success': success_callback,
            'request_failure': failedRequest,
            'request_extras': {}
        };
        if(post_starts_with){
            xhr_parameters['request_extras'][gatekeeper_options['startsWithParam']] = post_data;
        } else {
            xhr_parameters['request_extras'] = post_data;
        }
        

        // This is an authenticated request
        var gu_token = gu_auth.fn.getActiveToken();
        
        sendXHR(xhr_parameters, 'JSON_PUT', gu_token);
    }

    // Save the API data to browser storage
    processGatekeeperData(response, request_extras) {
        var this_component = this;
        var all_attributes = this_component['state']['configuration']['all_attributes'];

        var json_response = JSON.parse(response);
        var data_source = request_extras['data_source']
        var storage_key = squiz['vars']['store_id'] + data_source;

        // Save all user attributes
        sq_personalise.fn.setAttributes(all_attributes);

        // Special case for "userpersonalisation"
        if(data_source === 'userpersonalisation') {
            json_response['attributes'] = sq_personalise.fn.processAttributes(all_attributes, json_response['attributes']);
        }

        var storage_data = {
            "timestamp": new Date().getTime(),
            "data": json_response
        };
        
        // Save to session
        setStorage('session', storage_key, JSON.stringify(storage_data));
    }

    // All API data has been gathered
    allDataFound() {
        console.log('All data from Gatekeeper found');

        var this_component = this;
        var user_personalisation = squiz.fn.getStorage('userpersonalisation', 'session', 'json');
        
        // Save personalisation
        this_component.setState({
            'user_personalisation': user_personalisation['data']
        });

        // Get portal pages
        this_component.getPortalPages();
    }

    // Get all portal pages
    getPortalPages() {
        var this_component = this;
        var endpoints_container = document.querySelector('#endpoints');
        var page_list = endpoints_container.getAttribute('data-pages');

        var xhr_parameters = {
            'request_url': page_list,
            'request_success': this_component.processPortalPages,
            'request_failure': failedRequest,
            'request_extras': ""
        };

        sendXHR(xhr_parameters, 'GET');
    }

    // Save all portal pages and indicate the portal has finished loading
    processPortalPages(response) {
        var this_component = this;
        var all_pages = JSON.parse(response);

        this_component.setState({
            'all_pages': all_pages,
            'is_loading': false,
            'is_refreshing': false
        });
    }


    // 
    // Events
    //

    // Close the modal
    closeModal(event) {
        event.preventDefault();

        var this_component = this;

        this_component.closeModalManual();
    }

    closeModalManual() {
        var this_component = this;

        this_component.setState({
            'show_modal': false
        });
    }

    // Set content of the modal and show it
    setModalContent(modal_content) {
        var this_component = this;

        this_component.setState({
            'modal_content': modal_content,
            'show_modal': true
        });
    }

    // Get the page based on the URL
    getCurrentPage() {
        var this_component = this;
        var browser_query = window.location.hash.replace(/^#?|\/$/g, '');
        var current_page = 'home';
        var initial_character = browser_query.charAt(0);

        // Only do things if it's a page path
        if(initial_character === '' || initial_character === '/') {
            browser_query = browser_query.replace('/', '');
            browser_query = browser_query.split('/');

            // Handle home path
            if(browser_query[0] === 'home') {
                window.location.hash = '/';
            }

            // Look for a valid page if we're not home
            if(browser_query[0] !== '' && browser_query[0] !== 'home') {
                // for some sections, we need deep linking.
                var deep_links = [
                    'student-centre'
                ];
                // by default, do no allow
                var allow_deep_link = false;
                if(deep_links.indexOf(browser_query[0]) != -1){
                    // if there is a deep_links match
                    allow_deep_link = true;
                }

                if(allow_deep_link){
                    current_page = browser_query[0];
                } else {
                    for(var index = 0; index < browser_query.length; index++) {
                        var page_list = this_component.getPagesByIA(index + 1);
                        var page_found = this_component.isPageValid(browser_query[index], page_list);
    
                        if(page_found) {
                            current_page = browser_query[index];
                        } else {
                            current_page = '404';
                        }
                    }
                }
            }

            gu_auth.fn.saveLocation();

            this_component.setState({
                'current_page': current_page
            });
        }
    }

    getChildPages() {
        var this_component = this;
        var all_pages = this_component['state']['all_pages'];
        var current_page = this_component['state']['current_page'];
        var children = [];

        all_pages.forEach(function(page, page_index){
            var page_slug = page['slug'];

            if(current_page === page_slug) {
                children = page['children'];
            }
        });

        return children;
    }

    getPageBySlug(target_slug) {
        var this_component = this;
        var all_pages = this_component['state']['all_pages'];
        var target_page = null;

        // Handle home
        if(target_slug === 'home') {
            target_slug = '';
        }

        all_pages.forEach(function(page, page_index){
            var page_slug = page['slug'];

            if(target_slug === page_slug) {
                target_page = page;
            }

            // Check next level children
            page['children'].forEach(function(child_one, child_one_index){
                var child_one_slug = child_one['slug'];

                if(target_slug === child_one_slug) {
                    target_page = child_one;
                }
            });
        });

        return target_page;
    }

    updateNotificationCount(notification_count) {
        var this_component = this;

        this_component.setState({
            'notification_count': notification_count,
        })

    }


    // 
    // Render
    //

    render() {
        var this_component = this;
        var endpoints = this_component['state']['endpoints'];

        if(endpoints !== null) {
            return (
                <PortalContext.Provider value={this['state']}>
                    {this['props']['children']}
                </PortalContext.Provider>
            );
        }

        return null;
    }
};
